import { COMMERCIAL, FEDRAMP_MODERATE, ISOLATED_CLOUD_PERIMETERS } from '../../common/constants';
import { parseAtlCtxCookies } from '../atl-cookies-lookup';

/**
 * Determines if the current perimeter is an Isolated Cloud L2 perimeter
 *
 * @returns {boolean} True if the current perimeter is an Isolated Cloud perimeter, false otherwise
 */
export function isIsolatedCloud() {
  if (typeof document === 'undefined') {
    // @ts-ignore
    return globalThis.ssrContext.isInIC;
  }
  var atlCtxCookieValues = parseAtlCtxCookies();
  if (!atlCtxCookieValues) {
    // If the cookies are not set, the current perimeter is non-isolated commercial
    return false;
  }

  // The icDomain check is needed for when federal perimeters are eventually added to Isolated Cloud as well
  return ISOLATED_CLOUD_PERIMETERS.includes(atlCtxCookieValues.perimeter) && atlCtxCookieValues.icDomain !== undefined;
}

/**
 * Determines if the current perimeter is FedRAMP Moderate.
 * Please note that FedRAMP Moderate is not currently in Isolated Cloud, but when it is, this function will still return true.
 *
 * @returns {boolean} True if the current perimeter is FedRAMP Moderate, false otherwise
 */
export function isFedrampModerate() {
  if (typeof document === 'undefined') {
    // @ts-ignore
    return globalThis.ssrContext.isInFedramp;
  }
  var atlCtxCookieValues = parseAtlCtxCookies();
  if (!atlCtxCookieValues) {
    // If the cookies are not set, the current perimeter is non-isolated commercial
    return false;
  }
  return atlCtxCookieValues.perimeter === FEDRAMP_MODERATE;
}

/**
 * Retrieves the customer selected IC domain name.
 *
 *
 * @returns {string | undefined} The Isolated Cloud domain name if applicable, undefined otherwise (ex. if not in Isolated Cloud)
 */
export function isolatedCloudDomain() {
  if (typeof document === 'undefined') {
    return globalThis.location.hostname;
  }
  var atlCtxCookieValues = parseAtlCtxCookies();
  return atlCtxCookieValues === null || atlCtxCookieValues === void 0 ? void 0 : atlCtxCookieValues.icDomain;
}

/**
 * Returns the Isolation Context identifier
 *
 * @returns {string | undefined} The Isolation Context ID if applicable, undefined otherwise (ex. if not in Isolated Cloud)
 */
export function isolationContextId() {
  if (typeof document === 'undefined') {
    // @ts-ignore
    return isIsolatedCloud() ? globalThis.ssrContext.icName : undefined;
  }
  var atlCtxCookieValues = parseAtlCtxCookies();
  return atlCtxCookieValues === null || atlCtxCookieValues === void 0 ? void 0 : atlCtxCookieValues.icId;
}

/**
 * Returns information about the current environment for internal use
 * @returns {string | undefined} if the current environment is in isolated cloud + what the current perimeter is. Returns undefined when a new cloud environment has been created but not added to this library
 */
export function cloudEnvironment() {
  if (typeof document === 'undefined') {
    return cloudEnvironmentSsrLookup();
  }
  return cloudEnvironmentCookieLookup();
}
function cloudEnvironmentSsrLookup() {
  // @ts-ignore
  if (globalThis.ssrContext.isInIC === true) {
    return {
      type: 'isolated-cloud',
      perimeter: COMMERCIAL
    };
  }
  // @ts-ignore
  if (globalThis.ssrContext.isInFedramp === true) {
    return {
      type: 'non-isolated-cloud',
      perimeter: FEDRAMP_MODERATE
    };
  }
  return {
    type: 'non-isolated-cloud',
    perimeter: COMMERCIAL
  };
}
function cloudEnvironmentCookieLookup() {
  // Avoid calling isFedrampModerate() and isIsolatedCloud() here to avoid repeated cookie parsing
  var atlCtxCookieValues = parseAtlCtxCookies();
  if (!atlCtxCookieValues) {
    // If the cookies are not set, the current perimeter is non-isolated commercial
    return {
      type: 'non-isolated-cloud',
      perimeter: COMMERCIAL
    };
  }
  var isIc = ISOLATED_CLOUD_PERIMETERS.includes(atlCtxCookieValues.perimeter) && atlCtxCookieValues.icDomain !== undefined;
  if (isIc) {
    return {
      type: 'isolated-cloud',
      perimeter: COMMERCIAL
    };
  }
  if (atlCtxCookieValues.perimeter === FEDRAMP_MODERATE) {
    return {
      type: 'non-isolated-cloud',
      perimeter: FEDRAMP_MODERATE
    };
  }

  // If a new cloud environment has been created but not added to this library, return undefined
  return undefined;
}